"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";

export default function LoginPage() {
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Login failed");
      }

      const meRes = await fetch("/api/auth/me", {
        credentials: "include",
      });

      const me = await meRes.json();

      if (me.role === "customer") {
        router.push("/account");
      } else {
        router.push("/crm/dashboard");
      }

      router.refresh();
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-6 text-slate-100">

      {/* Logo Section */}
      <div className="mb-10 text-center">

        <div className="relative mx-auto mb-6 flex items-center justify-center">

          {/* Glow */}
          <div className="absolute h-48 w-48 rounded-full bg-cyan-400/20 blur-3xl" />

          {/* White glass badge */}
          <div className="relative flex h-36 w-36 items-center justify-center rounded-2xl bg-white/95 p-4 shadow-2xl shadow-cyan-500/20">

            <Image
              src="/horizon-logo.png"
              alt="Horizon Communication Services"
              width={120}
              height={120}
              className="object-contain"
              priority
            />

          </div>

        </div>

        <h1 className="text-4xl font-semibold tracking-tight">
          Horizon Communication Services
        </h1>

        <p className="mt-3 text-base text-slate-400">
          Customer Portal
        </p>

      </div>

      {/* Login Card */}
      <div className="w-full max-w-md rounded-3xl border border-slate-800 bg-slate-900/70 p-8 shadow-2xl shadow-slate-950/40">

        <form onSubmit={handleSubmit} className="space-y-4">

          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-2xl bg-slate-950 px-4 py-3 text-slate-100 outline-none ring-1 ring-slate-800 placeholder:text-slate-500"
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-2xl bg-slate-950 px-4 py-3 text-slate-100 outline-none ring-1 ring-slate-800 placeholder:text-slate-500"
          />

          {error && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl bg-cyan-500 px-5 py-3 font-semibold text-black transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

        </form>

        <div className="mt-6 text-center text-xs text-slate-500">
          Customers and staff sign in here
        </div>

      </div>

    </div>
  );
}
